import axios from "axios";

/**
 * Backend base URL — STRICT RULES:
 *
 *  1. On any hosted environment (preview, production, custom domain) we
 *     use `window.location.origin`. Emergent's ingress routes /api/* to
 *     the backend on the SAME origin the frontend is served from, so
 *     same-origin is the safe default. This avoids CORS + cross-origin
 *     cookie issues entirely.
 *
 *  2. Only when running locally (`yarn start` against localhost) do we
 *     fall back to REACT_APP_BACKEND_URL (so devs can point at
 *     localhost:8001).
 *
 *  3. We NEVER bake `REACT_APP_BACKEND_URL` into a production build,
 *     because if the custom domain is ever swapped (e.g. `answerwatch.com`
 *     replacing `*.emergent.host`) the old hardcoded URL would still
 *     leak into every API call. Same-origin auto-adapts.
 */
function resolveApiBase() {
  if (typeof window !== "undefined" && window.location) {
    const host = window.location.hostname;
    const isLocal = host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
    if (!isLocal) {
      return window.location.origin;
    }
  }
  // Local dev fallback
  return process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
}

export const API = `${resolveApiBase()}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Single shared "get an Auth0 access token" function. The AuthContext
// (which holds the Auth0 SDK instance) registers the real implementation
// on mount; until then, requests proceed without an Authorization header.
let _tokenGetter = null;
export function setTokenGetter(fn) {
  _tokenGetter = fn;
}

api.interceptors.request.use(async (config) => {
  if (_tokenGetter) {
    try {
      const token = await _tokenGetter();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token unavailable (e.g. user not logged in) — leave header off.
    }
  }
  return config;
});

export default api;
